import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { io, Socket } from 'socket.io-client';
import { 
    Check, Copy, ExternalLink, Loader2, MessageSquare, AlertTriangle, X, ChevronRight 
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface TeamTelegramWizardProps {
    teamId: string;
    onComplete: () => void;
    onBack?: () => void;
}

interface TelegramConfig {
    groupChatId: string | null;
}

interface LinkStatus {
    isLinked: boolean;
}

interface BotInfo {
    botUsername: string | null;
    isRunning: boolean;
    addToGroupLink: string | null;
}

export function TeamTelegramWizard({ teamId, onComplete, onBack }: TeamTelegramWizardProps) {
    const { t } = useTranslation();
    const [exiting, setExiting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [botInfo, setBotInfo] = useState<BotInfo | null>(null);
    const [config, setConfig] = useState<TelegramConfig | null>(null);
    const [linkStatus, setLinkStatus] = useState<LinkStatus | null>(null);

    const [setupCode, setSetupCode] = useState('');
    const [setupCodeCopied, setSetupCodeCopied] = useState(false);
    
    const [linkCode, setLinkCode] = useState('');
    const [linkCodeCopied, setLinkCodeCopied] = useState(false);

    const socketRef = useRef<Socket | null>(null);

    useEffect(() => {
        if (error) {
            const t = setTimeout(() => setError(''), 5000);
            return () => clearTimeout(t);
        }
    }, [error]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        Promise.all([
            axios.get(`${API}/telegram/bot-info`, { headers }),
            axios.get(`${API}/telegram/config/${teamId}`, { headers }),
            axios.get(`${API}/telegram/link/status/${teamId}`, { headers })
        ]).then(([botRes, confRes, linkRes]) => {
            setBotInfo(botRes.data);
            setConfig(confRes.data);
            setLinkStatus(linkRes.data);
        }).catch(err => {
            console.error('Failed to load telegram data', err);
            setError('Could not load Telegram integration status.');
        }).finally(() => {
            setLoading(false);
        });

        const socket = io(`${API}/telegram`, {
            auth: { token: localStorage.getItem('token') },
            query: { teamId },
            transports: ['websocket', 'polling'],
        });

        socket.on('configUpdate', (newConfig: any) => {
            setConfig(newConfig);
        });

        socket.on('linkUpdate', () => {
             axios.get(`${API}/telegram/link/status/${teamId}`, { headers })
                 .then((res) => setLinkStatus(res.data));
        });

        socketRef.current = socket;

        return () => {
            socket.disconnect();
        };
    }, [teamId]);

    const handleGenerateSetupCode = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API}/telegram/setup-code`, { teamId }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSetupCode(res.data.code);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to generate code.');
        }
    };

    const copySetupCode = () => {
        navigator.clipboard.writeText(`/setup ${setupCode}`);
        setSetupCodeCopied(true);
        setTimeout(() => setSetupCodeCopied(false), 2000);
    };

    const handleGenerateLinkCode = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API}/telegram/link/code`, { teamId }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLinkCode(res.data.code);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to generate code.');
        }
    };

    const copyLinkCode = () => {
        navigator.clipboard.writeText(`/link ${linkCode}`);
        setLinkCodeCopied(true);
        setTimeout(() => setLinkCodeCopied(false), 2000);
    };

    const handleSkip = () => {
        setExiting(true);
        setTimeout(() => onComplete(), 300);
    };

    const hasBotInGroup = !!config?.groupChatId;
    const isProfileLinked = !!linkStatus?.isLinked;
    const allCompleted = hasBotInGroup && isProfileLinked;

    return (
        <div className={`w-[800px] bg-slate-900 border border-slate-700/50 rounded-2xl shadow-[0_0_80px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden relative transform scale-100 transition-all duration-300 ${exiting ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
                
                {/* Header */}
                <div className="flex-shrink-0 px-6 py-6 border-b border-slate-700/30 text-center relative pointer-events-auto bg-slate-900 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-blue-500/10 to-transparent pointer-events-none" />
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 flex items-center justify-center mx-auto mb-3 shadow-[0_0_30px_rgba(59,130,246,0.3)]">
                        <img src="/icons/socials/Icon_Telegram.png" alt="Telegram" className="w-8 h-8" />
                    </div>
                    <h2 className="text-xl font-bold text-white tracking-tight relative z-10">Telegram Integration</h2>
                    <p className="text-slate-400 text-xs max-w-lg mx-auto mt-1 relative z-10">
                        Connect our companion bot to your Telegram group to receive instant notifications, control practice servers, and manage scheduling — all from within your chat.
                    </p>
                </div>

                {/* Content */}
                <div className="flex-1 bg-slate-900/50 p-8 space-y-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 text-slate-500">
                            <Loader2 className="w-8 h-8 animate-spin mb-4 text-blue-500" />
                            <p className="text-sm">Loading integration status...</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Step 1: Add Bot */}
                            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5 flex items-start gap-4">
                                <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/40 flex items-center justify-center shrink-0 mt-0.5">
                                    <span className="text-sm font-bold text-blue-400">1</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-bold text-white text-sm mb-1">Add StratSync Bot to Group</h3>
                                    <p className="text-xs text-slate-400 mb-3">Invite the bot to your team's Telegram group chat. You need to be an admin to do this.</p>
                                    <a 
                                        href={botInfo?.addToGroupLink || '#'} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-xs font-semibold text-white transition-colors"
                                    >
                                        <ExternalLink size={14} /> Open Telegram
                                    </a>
                                </div>
                                <div className="shrink-0 flex items-center h-10 px-4">
                                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Manual</span>
                                </div>
                            </div>

                            {/* Step 2: Setup Code */}
                            <div className={`bg-slate-800/40 border transition-colors duration-300 rounded-xl p-5 flex items-start gap-4 ${hasBotInGroup ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-slate-700/50'}`}>
                                <div className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 mt-0.5 transition-colors duration-300 ${hasBotInGroup ? 'bg-emerald-500 text-white border-emerald-400' : 'bg-blue-500/20 border-blue-500/40 text-blue-400 font-bold'}`}>
                                    {hasBotInGroup ? <Check size={16} strokeWidth={3} /> : <span className="text-sm">2</span>}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-bold text-white text-sm mb-1">Generate Setup Code</h3>
                                    <p className="text-xs text-slate-400 mb-3">Generate a code and send it as a message in your Telegram group to link it.</p>
                                    
                                    {!hasBotInGroup ? (
                                        setupCode ? (
                                            <div className="flex items-center gap-2">
                                                <code className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-blue-400 font-mono text-sm shadow-inner">
                                                    /setup {setupCode}
                                                </code>
                                                <button
                                                    onClick={copySetupCode}
                                                    className="p-2 rounded-lg bg-slate-800 border border-slate-600 hover:bg-blue-600 hover:border-blue-500 hover:text-white transition-all text-slate-300"
                                                >
                                                    {setupCodeCopied ? <Check size={16} /> : <Copy size={16} />}
                                                </button>
                                                <div className="ml-3 flex items-center gap-2 text-[10px] text-amber-500 font-mono animate-pulse">
                                                    <Loader2 size={12} className="animate-spin" /> waiting for message...
                                                </div>
                                            </div>
                                        ) : (
                                            <button 
                                                onClick={handleGenerateSetupCode}
                                                className="px-4 py-2 bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white border border-blue-500/30 rounded-lg text-xs font-semibold transition-all"
                                            >
                                                Generate Code
                                            </button>
                                        )
                                    ) : (
                                        <div className="text-xs font-bold text-emerald-400 flex items-center gap-2 bg-emerald-500/10 w-fit px-3 py-1.5 rounded-lg border border-emerald-500/20">
                                            <Check size={14} /> Group Linked Successfully
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Step 3: Link Profile */}
                            <div className={`bg-slate-800/40 border transition-colors duration-300 rounded-xl p-5 flex items-start gap-4 ${isProfileLinked ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-slate-700/50'}`}>
                                <div className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 mt-0.5 transition-colors duration-300 ${isProfileLinked ? 'bg-emerald-500 text-white border-emerald-400' : 'bg-blue-500/20 border-blue-500/40 text-blue-400 font-bold'}`}>
                                    {isProfileLinked ? <Check size={16} strokeWidth={3} /> : <span className="text-sm">3</span>}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-bold text-white text-sm mb-1">Link your Profile</h3>
                                    <p className="text-xs text-slate-400 mb-3">To identify you correctly, link your personal Telegram profile using this code in the group.</p>
                                    
                                    {!isProfileLinked ? (
                                        linkCode ? (
                                            <div className="flex items-center gap-2">
                                                <code className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-purple-400 font-mono text-sm shadow-inner">
                                                    /link {linkCode}
                                                </code>
                                                <button
                                                    onClick={copyLinkCode}
                                                    className="p-2 rounded-lg bg-slate-800 border border-slate-600 hover:bg-purple-600 hover:border-purple-500 hover:text-white transition-all text-slate-300"
                                                >
                                                    {linkCodeCopied ? <Check size={16} /> : <Copy size={16} />}
                                                </button>
                                                <div className="ml-3 flex items-center gap-2 text-[10px] text-amber-500 font-mono animate-pulse">
                                                    <Loader2 size={12} className="animate-spin" /> waiting for message...
                                                </div>
                                            </div>
                                        ) : (
                                            <button 
                                                onClick={handleGenerateLinkCode}
                                                disabled={!hasBotInGroup}
                                                className="px-4 py-2 bg-purple-600/20 hover:bg-purple-600 text-purple-400 hover:text-white border border-purple-500/30 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Generate Link Code
                                            </button>
                                        )
                                    ) : (
                                        <div className="text-xs font-bold text-emerald-400 flex items-center gap-2 bg-emerald-500/10 w-fit px-3 py-1.5 rounded-lg border border-emerald-500/20">
                                            <Check size={14} /> Profile Linked Successfully
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 p-5 border-t border-slate-700/30 flex items-center justify-between">
                    <div className="flex-1 flex items-center gap-4">
                        {onBack && (
                            <button 
                                className="team-wizard-back px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-white transition-colors"
                                onClick={() => {
                                    setExiting(true);
                                    setTimeout(() => onBack(), 300);
                                }}
                                disabled={loading}
                            >
                                Go Back
                            </button>
                        )}
                        {error && (
                            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-2 w-max animate-in fade-in">
                                <AlertTriangle size={12} />
                                {error}
                            </div>
                        )}
                    </div>
                    <div className="flex shrink-0">
                        {allCompleted ? (
                            <button 
                                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2.5 rounded-lg font-bold transition-colors shadow-[0_0_15px_rgba(16,185,129,0.4)] tracking-wider text-xs uppercase"
                                onClick={handleSkip} // Oncomplete basically
                            >
                                <Check size={16} />
                                Complete Telegram Integration
                            </button>
                        ) : (
                            <button 
                                className="team-wizard-skip text-xs uppercase text-slate-400 hover:text-white px-6 py-2.5 bg-slate-800 hover:bg-slate-700 rounded-lg font-bold tracking-wider transition-all border border-slate-700"
                                onClick={handleSkip}
                            >
                                Skip for now
                            </button>
                        )}
                    </div>
                </div>

        </div>
    );
}
